import {formatTime, getDateTime, moment} from "@/src/modules/core/lib";
import type {Me} from "@/src/modules/core/types";
import {useUserPropertyStore} from "@/src/modules/core/store/user-property";
import {shiftsService} from "@/src/modules/shifts/services";
import {EmergencyShiftSchema, Shift, ShiftBreak} from "@/src/modules/shifts/types";

import {useShiftsStore} from "../../shifts/store";
import {officerApi} from "../api";
import {getShiftBreaks} from "@/src/modules/shifts/lib/shift-breaks";
import {
  MAX_DELAYS,
  MAX_INTERRUPT,
  QUICK_ACTIONS_SECTIONS,
  SECTIONS,
  SENIOR_SUPERVISOR_EARLY_START_MINUTES,
} from "../lib/constants";
import {
  BreakActionRequest,
  ExtendShiftRequest,
  ShiftEndRequest,
  ShiftEndResponseData,
  ShiftStartRequest,
  ShiftStartResponseData,
} from "../types";

export function isBreakPastShiftEndError(error: unknown): boolean {
  const data = (error as {response?: {data?: {error_code?: string; message?: string}}})?.response
    ?.data;
  const errorCode = data?.error_code;
  const message = (data?.message ?? "").toLowerCase();
  return (
    errorCode === "break_extends_past_shift_end" || message.includes("extend past shift end")
  );
}

function markBreakPastShiftEndUnavailable(shift: Shift, shiftBreakId: number): Shift {
  return {
    ...shift,
    breaks: getShiftBreaks(shift).map((shiftBreak) => {
      if (shiftBreak.shift_break_id === shiftBreakId) {
        return {
          ...shiftBreak,
          is_take_disabled: 1 as const,
          can_reject: 1 as const,
        };
      }
      return shiftBreak;
    }),
  };
}

function parseShiftStartResponse(body: unknown, fallbackShiftId: number): ShiftStartResponseData {
  const asObj = (v: unknown) =>
    v != null && typeof v === "object" ? (v as Record<string, unknown>) : null;

  let row = asObj(body);
  if (row && "data" in row) {
    const inner = asObj(row.data);
    if (inner && typeof inner.shift_id === "number") {
      row = inner;
    }
  }
  if (row && typeof row.shift_id === "number") {
    return {
      shift_id: row.shift_id,
      worked_shift_id: typeof row.worked_shift_id === "number" ? row.worked_shift_id : undefined,
      status: typeof row.status === "string" ? row.status : undefined,
      actual_start_datetime: typeof row.actual_start_datetime === "string" ? row.actual_start_datetime : undefined,
      message: typeof row.message === "string" ? row.message : undefined,
      worked_shift: row.worked_shift,
    };
  }
  return {shift_id: fallbackShiftId};
}

function parseShiftEndResponse(body: unknown, fallbackShiftId: number): ShiftEndResponseData {
  const asObj = (v: unknown) =>
    v != null && typeof v === "object" ? (v as Record<string, unknown>) : null;

  let row = asObj(body);
  if (row && "data" in row) {
    const inner = asObj(row.data);
    if (inner && typeof inner.shift_id === "number") {
      row = inner;
    }
  }
  if (row && typeof row.shift_id === "number") {
    return {
      shift_id: row.shift_id,
      worked_shift_id:
        typeof row.worked_shift_id === "number" ? row.worked_shift_id : undefined,
      status: typeof row.status === "string" ? row.status : undefined,
      tokens_revoked: row.tokens_revoked === false ? false : true,
    };
  }
  return {shift_id: fallbackShiftId, tokens_revoked: true};
}

class OfficerService {
  async shiftStart(data: ShiftStartRequest): Promise<ShiftStartResponseData> {
    const {data: body} = await officerApi.shiftStart(data);
    return parseShiftStartResponse(body, data.shift_id);
  }

  async shiftEnd(data: ShiftEndRequest): Promise<ShiftEndResponseData> {
    const {data: body} = await officerApi.shiftEnd(data);
    const parsed = parseShiftEndResponse(body, data.shift_id);
    shiftsService.clearActiveShift();
    return parsed;
  }

  async breakAction(data: BreakActionRequest) {
    const activeShift = useShiftsStore.getState().activeShift;

    try {
      return await officerApi.breakAction(data);
    } catch (error: unknown) {
      if (isBreakPastShiftEndError(error) && activeShift) {
        useShiftsStore
          .getState()
          .setActiveShift(
            markBreakPastShiftEndUnavailable(activeShift, data.shift_break_id),
          );
      }
      throw error;
    }
  }

  extendShift(data: ExtendShiftRequest) {
    return officerApi.extendShift(data);
  }

  getIsDelayAvailable(shiftBreak: ShiftBreak) {
    return shiftBreak.delayed_count < MAX_DELAYS;
  }

  getIsInterruptAvailable(shiftBreak: ShiftBreak) {
    return shiftBreak.interrupted_count < MAX_INTERRUPT;
  }

  getCanReject(shiftBreak: ShiftBreak) {
    return shiftBreak.can_reject === 1;
  }

  getCanTakeBreak(shiftBreak: ShiftBreak) {
    return !shiftBreak.is_take_disabled;
  }

  getQuickActionsSections() {
    const {userProperty} = useUserPropertyStore.getState();
    const sections = userProperty
      ? userProperty.sections
          .filter(
            (s) =>
              QUICK_ACTIONS_SECTIONS.includes(s.key as keyof typeof SECTIONS) && s.granted === 1,
          )
          .map((s) => s.key)
      : [];

    const sectionsSet = new Set(sections as typeof QUICK_ACTIONS_SECTIONS);

    return sectionsSet;
  }

  getIsBreakAvailable(shiftBreak: ShiftBreak) {
    const now = moment();
    const breakDate = moment(shiftBreak.execution_start_datetime);

    return (
      (breakDate.isSameOrBefore(now) && shiftBreak.status === "scheduled") ||
      shiftBreak.status === "active"
    );
  }

  getBreakRemainingTime(shiftBreak: ShiftBreak) {
    if (!shiftBreak.execution_start_datetime) return null;

    const minutes = shiftBreak.duration_minutes;
    if (!Number.isFinite(minutes) || minutes <= 0) return null;

    const start = moment(shiftBreak.execution_start_datetime);
    const end = moment(start).add(minutes, "minutes");
    const now = moment();
    const rawRemainingMs = end.diff(now);
    const maxMs = minutes * 60 * 1000;
    // Cap so we never show more than the scheduled break length (e.g. when execution start
    // appears "in the future" vs the client clock, which used to yield huge "remaining" times).
    const remainingMs = Math.max(0, Math.min(rawRemainingMs, maxMs));
    return moment.duration(remainingMs, "milliseconds");
  }

  getEarliestAllowedShiftStart(shift: Pick<Shift, "from_datetime">, me: Me | null | undefined) {
    const scheduledStart = moment(shift.from_datetime);
    if (me?.isSeniorSupervisor) {
      return scheduledStart.clone().subtract(SENIOR_SUPERVISOR_EARLY_START_MINUTES, "minutes");
    }
    return scheduledStart;
  }

  canStartShiftNow(
    shift: Pick<Shift, "from_datetime">,
    me: Me | null | undefined,
    now = moment(),
  ) {
    return now.isSameOrAfter(this.getEarliestAllowedShiftStart(shift, me));
  }

  isSeniorSupervisorEarlyStartWindow(
    shift: Pick<Shift, "from_datetime">,
    me: Me | null | undefined,
    now = moment(),
  ) {
    if (!me?.isSeniorSupervisor) return false;
    const scheduledStart = moment(shift.from_datetime);
    return (
      now.isSameOrAfter(
        scheduledStart.clone().subtract(SENIOR_SUPERVISOR_EARLY_START_MINUTES, "minutes"),
      ) && now.isBefore(scheduledStart)
    );
  }

  /** Whole minutes before scheduled start when starting at `now`; `0` if at or after scheduled start. */
  getShiftEarlyStartMinutes(shift: Pick<Shift, "from_datetime">, now = moment()) {
    const scheduledStart = moment(shift.from_datetime);
    if (!now.isBefore(scheduledStart)) return 0;
    return Math.ceil(scheduledStart.diff(now, "minutes", true));
  }

  /** Scheduled end shifted earlier by the same amount as an early clock-in at `startMoment`. */
  getShiftEndIfStartedAt(
    shift: Pick<Shift, "from_datetime" | "to_datetime">,
    startMoment = moment(),
  ) {
    const scheduledStart = moment(shift.from_datetime);
    const scheduledEnd = moment(shift.to_datetime);
    const earlyMinutes = this.getShiftEarlyStartMinutes(shift, startMoment);
    if (earlyMinutes <= 0) return scheduledEnd;
    return scheduledEnd.clone().subtract(earlyMinutes, "minutes");
  }

  getEarlyStartEndAdjustmentNotice(
    shift: Pick<Shift, "from_datetime" | "to_datetime">,
    now = moment(),
    compact = false,
  ): string | null {
    const earlyMinutes = this.getShiftEarlyStartMinutes(shift, now);
    if (earlyMinutes <= 0) return null;

    const scheduledEnd = moment(shift.to_datetime);
    const adjustedEnd = this.getShiftEndIfStartedAt(shift, now);
    const endTime = formatTime(adjustedEnd.toDate());
    const scheduledEndTime = formatTime(scheduledEnd.toDate());
    const minuteLabel = earlyMinutes === 1 ? "minute" : "minutes";

    if (compact) {
      return `Ends at ${endTime} instead of ${scheduledEndTime} (${earlyMinutes} min earlier).`;
    }

    return `Starting now ends your shift at ${endTime} instead of ${scheduledEndTime} (${earlyMinutes} ${minuteLabel} earlier).`;
  }

  getNextAndUpcomingShifts() {
    const shifts = useShiftsStore.getState().shifts;
    const activeShift = useShiftsStore.getState().activeShift;
    if (activeShift) {
      return {
        nextShift: null,
        upcomingShifts: shifts,
      };
    }

    return {
      nextShift: shifts[0],
      upcomingShifts: shifts.slice(1),
    };
  }

  hasOverlapWithNextShift(data: EmergencyShiftSchema) {
    const {nextShift} = this.getNextAndUpcomingShifts();

    if (!nextShift) return false;
    return getDateTime(data.to_date, data.to_time).isSameOrAfter(moment(nextShift.from_datetime));
  }
}

export const officerService = new OfficerService();
